import { z } from 'zod';
import type { Entity, Viewer } from 'cesium';
import type { ReactNode } from 'react';
import { createContext, useContext, useState } from 'react';
import type { LatLngHeightHPR } from '~/components/cesium/CesiumViewer';
import invariant from 'tiny-invariant';
import type { TilesetParams } from '~/components/cesium/cesiumUtils';
import type { SlideType } from '~/routes/upload/vft-manager/$vftId';
import type { VftManagerVftType } from '~/routes/upload/vft-manager/vft-manager';
import { LifecycleStatus } from '~/apollo/generated/v4/graphql';

export const vftSchema = z.object({
  name: z.string().min(1),
  outcrops: z.array(z.number()).optional(),
  outcropTour: z.boolean(),
  lifecycleStatus: z.nativeEnum(LifecycleStatus),
});
export type VftFormValues = {
  name: string;
  outcrops?: number[];
  outcropTour: boolean;
  lifecycleStatus: LifecycleStatus;
};
export function initialVft(vft?: VftManagerVftType): VftFormValues {
  const outcrops = vft?.outcrops?.map(oc => Number(oc.id)) || [];
  return {
    name: vft?.name ?? '',
    outcrops,
    outcropTour: vft?.outcropTour ?? false,
    lifecycleStatus: vft?.lifecycleStatus ?? LifecycleStatus.Draft,
  };
}

export const vftSlideSchema = z.object({
  title: z.string().min(1),
  text: z.string().min(1),
});
export type VftSlideFormValues = {
  title: string;
  text: string;
};

type VftPageContextType = {
  viewer: Viewer | null;
  setViewer: (viewer: Viewer) => void;
  slides: SlideType[];
  setSlides: (slides: SlideType[]) => void;
  currentCamera: LatLngHeightHPR | null;
  setCurrentCamera: (position: LatLngHeightHPR) => void;
  currentSlideId: string | null;
  setCurrentSlideId: (id: string) => void;
  visibleTilesets: TilesetParams[];
  setVisibleTilesets: (ts: TilesetParams[]) => void;
  hiddenTilesets: TilesetParams[];
  setHiddenTilesets: (ts: TilesetParams[]) => void;
  initialEntities: Entity[];
  setInitialEntities: (e: Entity[]) => void;
  showLabels: boolean;
  setShowLabels: (show: boolean) => void;
};

export const VftPageContext = createContext<VftPageContextType | null>(null);

export function useVftPageContext() {
  const ctx = useContext(VftPageContext);
  invariant(ctx, 'PlacementPageContext not initialized!');
  return ctx;
}

export function VftPageContextProvider({ children }: { children: ReactNode }) {
  const [currentCamera, setCurrentCamera] = useState<LatLngHeightHPR | null>(
    null,
  );
  const [viewer, setViewer] = useState<Viewer | null>(null);
  const [currentSlideId, setCurrentSlideId] = useState<string | null>(null);
  const [visibleTilesets, setVisibleTilesets] = useState<TilesetParams[]>([]);
  const [hiddenTilesets, setHiddenTilesets] = useState<TilesetParams[]>([]);
  const [initialEntities, setInitialEntities] = useState<Entity[]>([]);
  const [slides, setSlides] = useState<SlideType[]>([]);
  const [showLabels, setShowLabels] = useState<boolean>(true);

  return (
    <VftPageContext.Provider
      value={{
        viewer,
        setViewer,
        slides,
        setSlides,
        currentCamera,
        setCurrentCamera,
        currentSlideId,
        setCurrentSlideId,
        visibleTilesets,
        setVisibleTilesets,
        hiddenTilesets,
        setHiddenTilesets,
        initialEntities,
        setInitialEntities,
        showLabels,
        setShowLabels
      }}
    >
      {children}
    </VftPageContext.Provider>
  );
}
